import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit";
import { api, apiErrorsHandler } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { setCurrentUser } from "./currentuserslice";
import { notificationDurationShort, notificationDurationMiddle, notificationDurationLong } from "../../lib/utils";
import { canCreateReports, canEditReports, canDeleteReports } from "../../routing/authorizationhandlers";

const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    filter: "", showSystemEntities: true,
    items: [],
    canCreate: false, canEdit: false, canDelete: false,
    panes: [                    
        {
            // size: "30%",
            size:"25%",
            min: "25%",
            max: "25%",
            collapsible: false
        },           
        {
            //min: "700px",
            //size:"60%",
            min: "75%",
            max: "75%",
            collapsible: false,
        }
    ]
};

const reportingLocatorPageSlice = createSlice({
    name: "reportingLocatorPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPanes: (state, action) => ({...state, panes: {...action.payload}}),

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),
              
        pageLoaded : (state, action) => {
            //payload: {reports: , currentUser: ,... }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                items: action.payload.reports, 
                canCreate: canCreateReports(action.payload.currentUser), 
                canEdit: canEditReports(action.payload.currentUser), 
                canDelete: canDeleteReports(action.payload.currentUser), 
                pageDisposed: false
            };
        },       

        favoriteSet: (state, action) => {
            //payload: { id: , value: }
            const id = action.payload.id;
            const value = action.payload.value;

            return ({...state, items: state.items.map(e => {
                if (e.Id == id)
                    return {...e, IsFavorite: value};
                else
                    return {...e};
            })});
        },

        entityDeleted: (state, action) => {
            //payload: the entity referenceKey
            let pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            let entityReferenceKey = action.payload;
            let newItems = pageState.items.filter(e => e.ReferenceKey != entityReferenceKey);
            
            return ({
                ...state, 
                items: newItems
            });
        },

        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default reportingLocatorPageSlice.reducer;


export const loadPage = (currentUser, t) => async (dispatch, getState) => {
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: 'reportingdesign/getreportslist',
                method:  'post',
                data: {
                },
                //headers:{'h':'1'}
            });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingLocatorPage.pageDisposed)
            return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response
        dispatch(pageLoaded(
        {
            reports: data.Reports.map(e => 
            {
                const _iconId = e.IsDashboard ? 0 : (e.MainWidgetTypeId >= 30 && e.MainWidgetTypeId <= 41 ? 9999 : e.MainWidgetTypeId);

                return {
                    ...e, 
                    IsVisible: true,
                    IconId: _iconId
                };
            }), 

            currentUser: currentUser
        })
        );

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const setFavorite = (id, t) => async (dispatch, getState) => {
    //payload the entity id
    const pageState = getState().reportingLocatorPage;

    dispatch(setProcessing({processing: true, processingText: t("slices.settingFavorite")}));

    const isFavorite = pageState.items.find(e => e.Id == id).IsFavorite;

    try{
        var response = {data:{}};

        var response = await api({            
            url:"/reportingdesign/favoriteReport",
            method:  'post',
            data: {reportId: id, value: !isFavorite},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingLocatorPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(addNotification({text: t("slices.operationCompleted"), type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

        dispatch(favoriteSet({id: id, value: !isFavorite}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const deleteEntity = (referenceKey, t) => async (dispatch, getState) => {
    //payload the entity referenceKey
    dispatch(setProcessing({processing: true, processingText: t("slices.deletingReport")}));

    try{
        var response = await api({            
            url:"/reportingdesign/removereport",
            method:  'post',
            data: {referenceKey: referenceKey},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingLocatorPage.pageDisposed)
           return;

        let data = response.data.Data;

        let currentUser = getState().currentUser;

        dispatch(addNotification({text: t("slices.deletedReport"), type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

        dispatch(setCurrentUser({...currentUser, ReportsMenu: currentUser.ReportsMenu.filter(e => e.referenceKey != referenceKey)}))

        dispatch(setProcessing({processing: false}));

        dispatch(entityDeleted(referenceKey));
 
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};


export const { setInitialState, setProcessing, setPageValue, setPanes, pageLoaded, entityDeleted, favoriteSet, disposePage } = reportingLocatorPageSlice.actions;

